// The shape of a selection result for the Activities dashboard and the pure helpers the dashboard shares: the
// figures of a row, the words around a result, time shown as UTC with the local time in brackets, and the bands of
// the distributions. The demo build fills it from its own API; here utils/catalogueActivities.ts fills it from the
// data API's rows. Pure: no React, router or Firebase imports.
import type { ActivityMeasure } from '../config/products';

export const EE_MODES = ['car', 'walk', 'bike', 'transit', 'other'] as const;
export type EeMode = (typeof EE_MODES)[number];

/** The text a withheld figure (fewer than 5) is written as. Never 0 and never a dash. */
export const WITHHELD = '<5';

export type EeRole = 'counted' | 'inside_counted';

export interface EeRow {
  geo_id: string;
  geo_name: string | null;
  hour_of_day: number | null;
  /** The size of a grid cell in metres; null for the admin levels. */
  size_m: number | null;
  k5_tier: string | null;
  /** counted: summed into the result. inside_counted: a shown cell inside a counted cell, not summed. */
  role: EeRole;
  /** For inside_counted: the id of the counted cell that includes it. */
  counted_in: string | null;
  /** For a counted grid cell: the selected map cells it covers. */
  cells: string[];
  sum_unique_people: number | null;
  arrivals: number | null;
  average_distance: number | null;
  average_travel_time: number | null;
  average_visit_length: number | null;
  arrivals_car_modelled: number | null;
  arrivals_walk_modelled: number | null;
  arrivals_bike_modelled: number | null;
  arrivals_transit_modelled: number | null;
  arrivals_other_modelled: number | null;
}

export interface EeSummary {
  hour_of_day: number | null;
  people: number | null;
  arrivals: number | null;
  modes: Record<EeMode, number | null>;
  average_distance: number | null;
  average_travel_time: number | null;
  average_visit_length: number | null;
  /** Covered area over selected area (grid); null when the result does not say (admin levels, and the data API). */
  coverage: number | null;
  covered_km2: number | null;
  uncovered_km2: number | null;
  /** How many selected map cells no counted cell covers. */
  uncovered_cells: number;
}

/** What "include more cells" adds: the map cells, and the shown cells that hold them. */
export interface EeGrow {
  cells_to_add: string[];
  via: { geo_id: string; size_m: number; people: number | null }[];
  note?: string;
}

// origin_lau: catalogue 1.0, the areas arrivals come from (in the country's own ids).
export const EE_DISTRIBUTION_MEASURES = ['distance', 'travel_time', 'visit_length', 'purpose', 'origin_settlement', 'origin_municipality', 'origin_lau'] as const;
export type EeDistributionMeasure = (typeof EE_DISTRIBUTION_MEASURES)[number];
/** The roles the distributions split arrivals by; Everyone is their sum. */
export const EE_ROLES = ['home', 'work', 'school', 'elsewhere'] as const;
export type EeRoleGroup = (typeof EE_ROLES)[number];
export type EeDistributionGroup = 'everyone' | EeRoleGroup;
export const EE_DISTRIBUTION_GROUPS: readonly EeDistributionGroup[] = ['everyone', ...EE_ROLES];

/** One band of a distribution, modelled and shown in full (docs/adr/0001). A null figure is one the table does not hold. */
export interface EeDistributionBand {
  /** The band's key. */
  band: string;
  name: string;
  min: number | null;
  max: number | null;
  arrivals: number | null;
  people: number | null;
  /** The band's arrivals over the distribution's arrivals (0 to 1). */
  share: number | null;
}

/** How the arrivals of one hour (null = the day) split across the bands of one measure, for one group. Modelled. */
export interface EeDistribution {
  hour: number | null;
  group: EeDistributionGroup;
  measure: EeDistributionMeasure;
  unit: string;
  arrivals: number | null;
  bands: EeDistributionBand[];
}

/** The day's arrivals per group as a daily request would give them (hourly answers only). */
export type EeDayArrivals = Record<EeDistributionGroup, number | null>;

/** A selection result. */
export interface EeResponse {
  geo_type: string;
  date: string;
  type: 'daily' | 'hourly';
  rows: EeRow[];
  summary: EeSummary[];
  grow: EeGrow | null;
  distributions?: EeDistribution[];
  /** Why the answer has no distributions although the area may have some. */
  distributions_unavailable?: string;
  /** Hourly answers only: the day's arrivals for the same selection, computed as a daily request does. */
  day_arrivals?: EeDayArrivals;
  /** The summaries' people add up several areas, so someone in more than one is counted in each: not a count of distinct people. */
  people_is_sum_of_areas?: boolean;
  /** The hour with the most people present and how many: minutes spent there over 60, which add up across areas. Left out without dwell figures. */
  busiest_time?: { hour: number; people: number };
}

/** An answer with what the page needs to word it. */
export interface EeJobResult extends EeResponse {
  /** The ids the request named (including any added to complete the result). */
  requestedIds: string[];
  /** How many areas the request named. */
  areaCount: number;
  /** How many cells were added to complete the result; 0 when the selection is the person's own. */
  addedCells: number;
}

// ── Figures ───────────────────────────────────────────────────────────────────────────────────────

/** The Activities measures a selection result has a figure for (the dashboard's "Show on map" offers no others). */
export const EE_MEASURES: readonly ActivityMeasure[] = ['sum_unique_people', 'arrivals', 'average_distance', 'average_travel_time', 'average_visit_length'];

/** The figure of an Activities measure on one row (null = withheld). */
export function eeRowValue(r: EeRow, m: ActivityMeasure): number | null {
  switch (m) {
    case 'sum_unique_people': return r.sum_unique_people;
    case 'arrivals': return r.arrivals;
    case 'average_distance': return r.average_distance;
    case 'average_travel_time': return r.average_travel_time;
    case 'average_visit_length': return r.average_visit_length;
    default: return null;
  }
}

/** The figure of an Activities measure in one summary (null = withheld). */
export function eeSummaryValue(s: EeSummary, m: ActivityMeasure): number | null {
  switch (m) {
    case 'sum_unique_people': return s.people;
    case 'arrivals': return s.arrivals;
    case 'average_distance': return s.average_distance;
    case 'average_travel_time': return s.average_travel_time;
    case 'average_visit_length': return s.average_visit_length;
    default: return null;
  }
}

/** A count written for people: the number, or <5 when it is withheld. */
export function countText(v: number | null | undefined): string {
  return v == null ? WITHHELD : v.toLocaleString();
}

/** A count rounded to `digits` significant digits, for a figure that is not exact; <5 when it is withheld. */
export function aboutText(v: number | null | undefined, digits: number): string {
  if (v == null) return WITHHELD;
  if (v === 0) return '0';
  const scale = 10 ** Math.max(0, Math.floor(Math.log10(Math.abs(v))) + 1 - digits);
  return (Math.round(v / scale) * scale).toLocaleString();
}

/** An average written for people: at most one decimal, or <5 when it is withheld. */
export function averageText(v: number | null | undefined): string {
  return v == null ? WITHHELD : v.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

/** The row's figure for a measure as text: counts as whole numbers, averages with a decimal, withheld as <5. */
export function eeValueText(v: number | null | undefined, m: ActivityMeasure): string {
  return m === 'sum_unique_people' || m === 'arrivals' ? countText(v) : averageText(v);
}

/** True for the grids whose figures count the largest shown cells inside a selection: Estonia's and the catalogue's model grid. */
export function isEeGridType(geoType: string | null | undefined): boolean {
  return geoType === 'adaptive_grid' || geoType === 'model_grid';
}

/** "Coverage 87%". The share rounds down, so 99.6% never reads as 100%. */
export function coverageText(coverage: number): string {
  return `Coverage ${Math.floor(coverage * 100 + 1e-9)}%`;
}

/** "3 cells (0.2 km²) of your selection aren't covered: their people are only published as part of a larger cell." */
export function uncoveredText(uncoveredCells: number, uncoveredKm2: number): string {
  const km2 = uncoveredKm2.toLocaleString(undefined, { maximumFractionDigits: uncoveredKm2 < 10 ? 2 : 0 });
  const cells = `${uncoveredCells.toLocaleString()} ${uncoveredCells === 1 ? 'cell' : 'cells'}`;
  return `${cells} (${km2} km²) of your selection ${uncoveredCells === 1 ? "isn't" : "aren't"} covered: their people are only published as part of a larger cell.`;
}

/** The coverage line: "Coverage 84% · n cells (x km²) of your selection aren't covered: ...". */
export function coverageLine(coverage: number, uncoveredCells: number, uncoveredKm2: number): string {
  return uncoveredCells <= 0 ? coverageText(coverage) : `${coverageText(coverage)} · ${uncoveredText(uncoveredCells, uncoveredKm2)}`;
}

// ── Time: UTC with the local time in brackets ─────────────────────────────────────────────────────

const localFormatters = new Map<string, Intl.DateTimeFormat>();

/** "08:00" in `zone` at instant `ms` (the zone comes from the country registry, never a fixed offset). */
export function localClock(ms: number, zone: string): string {
  let f = localFormatters.get(zone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-GB', { timeZone: zone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
    localFormatters.set(zone, f);
  }
  const parts = f.formatToParts(new Date(ms));
  const hh = parts.find((p) => p.type === 'hour')?.value ?? '00';
  const mm = parts.find((p) => p.type === 'minute')?.value ?? '00';
  return `${hh === '24' ? '00' : hh}:${mm}`;
}

const clock = (ms: number): string => {
  const d = new Date(ms);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
};

/** "05:00 UTC (08:00 local)". */
export function utcWithLocal(ms: number, zone: string): string {
  return `${clock(ms)} UTC (${localClock(ms, zone)} local)`;
}

// ── Distributions ─────────────────────────────────────────────────────────────────────────────────

export const EE_OTHER_SETTLEMENTS = 'Other settlements';
export const EE_OTHER_AREAS = 'Other areas';
export const EE_ORIGIN_TOP = 15;

/** The distribution for one hour (null = the day), group and measure; undefined when there is none. */
export function selectDistribution(
  distributions: readonly EeDistribution[] | undefined,
  hour: number | null,
  group: EeDistributionGroup,
  measure: EeDistributionMeasure,
): EeDistribution | undefined {
  return (distributions ?? []).find((d) => d.hour === hour && d.group === group && d.measure === measure);
}

/**
 * The arrivals of a group over all the hours that have a distribution: per hour the first measure with a figure
 * (every measure of an hour adds up to the same arrivals). null when no hour has one.
 */
export function hourlyDistributionArrivals(distributions: readonly EeDistribution[] | undefined, group: EeDistributionGroup): number | null {
  const perHour = new Map<number, number>();
  for (const d of distributions ?? []) {
    if (d.group === group && d.hour !== null && d.arrivals !== null && !perHour.has(d.hour)) perHour.set(d.hour, d.arrivals);
  }
  return perHour.size === 0 ? null : sumSkippingNull(Array.from(perHour.values()));
}

/** "Hours with published arrivals add up to 1,730 of the day's 1,739 arrivals." */
export function publishedHoursText(hoursSum: number, dayArrivals: number): string {
  return `Hours with published arrivals add up to ${hoursSum.toLocaleString()} of the day's ${dayArrivals.toLocaleString()} arrivals.`;
}

/** Sums arrivals, skipping nulls; null when every one is null. */
function sumSkippingNull(values: (number | null)[]): number | null {
  let total: number | null = null;
  for (const v of values) if (v !== null) total = (total ?? 0) + v;
  return total;
}

/**
 * Keeps the first `top` bands (already in arrivals order) with their own values and folds the rest into one
 * "Other settlements" band at the end. Nothing is folded when there are no more than `top`; the shares stay shares
 * of the whole distribution.
 */
export function foldOrigins(bands: readonly EeDistributionBand[], top: number = EE_ORIGIN_TOP, otherName: string = EE_OTHER_SETTLEMENTS): EeDistributionBand[] {
  if (bands.length <= top) return [...bands];
  const rest = bands.slice(top);
  const other: EeDistributionBand = {
    band: 'other',
    name: otherName,
    min: null,
    max: null,
    arrivals: sumSkippingNull(rest.map((b) => b.arrivals)),
    people: sumSkippingNull(rest.map((b) => b.people)),
    share: sumSkippingNull(rest.map((b) => b.share)),
  };
  return [...bands.slice(0, top), other];
}

/** The bands a panel draws: origins fold to the top 15 plus the rest in one band; the others as they are. */
export function panelBands(d: EeDistribution): EeDistributionBand[] {
  if (d.measure === 'origin_settlement') return foldOrigins(d.bands);
  if (d.measure === 'origin_lau') return foldOrigins(d.bands, EE_ORIGIN_TOP, EE_OTHER_AREAS);
  return d.bands;
}

export const EE_MODE_LABELS: Record<EeMode, string> = { car: 'Car', walk: 'Walk', bike: 'Bike', transit: 'Transit', other: 'Other' };

/**
 * The arrivals by mode of a summary as bands, in the order of EE_MODES, each with its share of the modes' total.
 * Modes the summary has no figure for are left out. Modelled, like every distribution.
 */
export function modeBands(summary: EeSummary | undefined): EeDistributionBand[] {
  if (!summary) return [];
  const present = EE_MODES.filter((m) => summary.modes[m] !== null);
  const total = sumSkippingNull(present.map((m) => summary.modes[m]));
  return present.map((m) => {
    const arrivals = summary.modes[m];
    return {
      band: m,
      name: EE_MODE_LABELS[m],
      min: null,
      max: null,
      arrivals,
      people: null,
      share: total === null || arrivals === null || total === 0 ? null : Math.round((arrivals / total) * 1e4) / 1e4,
    };
  });
}

/** "12.3%"; empty when the table holds no share for the band. */
export function shareText(share: number | null | undefined): string {
  return share == null ? '' : `${(share * 100).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
}
